import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  ButtonGroup,
  Input,
  ModalProps,
  Stack,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { DangerButton } from 'components/buttons';
import { FormControl } from 'components/Form/FormControl';
import React, { PropsWithChildren, useEffect, useState } from 'react';
import { Action, DestructiveAction, isDestructiveAction } from 'types/actions';

interface ConfirmationRequestProps {
  confirmationText: string;
  confirmationValue: string;
  onConfirmationChange: (hasConfirmed: boolean) => void;
  focusRef: React.MutableRefObject<null>;
}

const ConfirmationRequest = ({
  confirmationText,
  confirmationValue,
  onConfirmationChange,
  focusRef,
}: ConfirmationRequestProps) => {
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    onConfirmationChange(inputValue === confirmationValue);
  }, [inputValue, confirmationValue, onConfirmationChange]);

  return (
    <Box>
      <FormControl label={confirmationText}>
        <Input
          type="text"
          value={inputValue}
          ref={focusRef}
          autoFocus={true}
          onChange={({ target: { value } }) => setInputValue(value)}
        />
      </FormControl>
    </Box>
  );
};

export interface ConfirmationDialogProps extends Omit<ModalProps, 'children'> {
  action: Action | DestructiveAction;
  cancelText?: string;
  confirmationText?: string;
}

const ConfirmationDialog = ({
  isOpen,
  onClose,
  children,
  action,
  cancelText,
  confirmationText,
}: PropsWithChildren<ConfirmationDialogProps>) => {
  const initialFocusRef = React.useRef(null);
  const [isConfirmed, setIsConfirmed] = useState(false);

  const showConfirmation =
    isDestructiveAction(action) && !!action.confirmationValue;
  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={initialFocusRef}
      onClose={onClose}
      isCentered
      size="sm"
      data-qa="confirmation-dialog"
    >
      <AlertDialogOverlay>
        <AlertDialogContent mx="20px" data-qa="confirmation-dialog-content">
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            {action.name}
          </AlertDialogHeader>

          <AlertDialogBody>
            <Stack spacing={4}>
              <Box>{children}</Box>
              {showConfirmation && (
                <ConfirmationRequest
                  confirmationText={action.askForConfirmationText}
                  confirmationValue={action.confirmationValue}
                  onConfirmationChange={setIsConfirmed}
                  focusRef={initialFocusRef}
                />
              )}
            </Stack>
          </AlertDialogBody>

          <AlertDialogFooter>
            <ButtonGroup spacing={2} w="100%">
              <Button
                ref={!showConfirmation ? initialFocusRef : null}
                onClick={onClose}
                flexGrow={0.5}
              >
                {cancelText || t`Cancel`}
              </Button>
              <DangerButton
                flexGrow={0.5}
                onClick={() => action.onAction()}
                isLoading={action.inProgress}
                isDisabled={showConfirmation && !isConfirmed}
                aria-label={confirmationText}
              >
                {confirmationText || `Proceed`}
              </DangerButton>
            </ButtonGroup>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default ConfirmationDialog;
